import { inject, Injectable } from "@angular/core";
import { Capacitor, CapacitorException } from "@capacitor/core";
import {
  AccessControl,
  BiometricAuthError,
  BiometryType as NativeBiometryType,
  NativeBiometric,
} from "@capgo/capacitor-native-biometric";
import { firstValueFrom } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { AuthApi } from "../auth.api";
import { AuthStore } from "../auth.store";
import { ToastService } from "../../services/toast.service";
import { IsRoot } from "@capgo/capacitor-is-root";
import { tryDecodeJwtPayload } from "../jwt";

export type BiometricType = "staff" | "socio";

interface VaultPayload {
  access_token: string;
  enrolledAt: number;
}

function decodeJwtExp(token: string): number | null {
  const payload = tryDecodeJwtPayload<{ exp?: number }>(token);
  return payload && typeof payload.exp === "number" ? payload.exp : null;
}

@Injectable({ providedIn: "root" })
export class BiometricService {
  private readonly authApi = inject(AuthApi);
  private readonly authStore = inject(AuthStore);
  private readonly toast = inject(ToastService);

  private biometryChangeHandle: { remove: () => Promise<void> } | null = null;
  private biometryChangeRegistered = false;
  private _lastCode: number | null = null;

  keyFor(type: BiometricType): string {
    return `tenso.session.${type}.token`;
  }

  getLastErrorCode(): number | null {
    return this._lastCode;
  }

  clearLastError(): void {
    this._lastCode = null;
  }

  async isAvailable(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return false;
    try {
      const res = await IsRoot.isRooted().catch(() => null);
      const isRooted = res
        ? ((
            res as unknown as {
              value?: boolean;
              result?: boolean;
              isRooted?: boolean;
            }
          ).value ??
          (res as unknown as { result?: boolean }).result ??
          (res as unknown as { isRooted?: boolean }).isRooted ??
          false)
        : false;
      if (isRooted) return false;
    } catch {
      // ignore IsRoot failure — treat as not rooted
    }
    try {
      const r = await NativeBiometric.isAvailable();
      return r.isAvailable && r.strongBiometryIsAvailable;
    } catch {
      return false;
    }
  }

  async getBiometryType(): Promise<NativeBiometryType | null> {
    if (!Capacitor.isNativePlatform()) return null;
    try {
      const r = await NativeBiometric.isAvailable();
      return r.biometryType ?? null;
    } catch {
      return null;
    }
  }

  async isEnrolled(type: BiometricType): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return false;
    try {
      const { isSaved } = await NativeBiometric.isDataSaved({
        key: this.keyFor(type),
      });
      return isSaved;
    } catch {
      return false;
    }
  }

  async enroll(type: BiometricType): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    const token = this.authStore.accessToken();
    if (!token) throw new Error("no-token");

    // Availability gate blocks weak-only biometrics
    const available = await this.isAvailable();
    if (!available) throw new Error("BIOMETRY_NOT_AVAILABLE");

    await NativeBiometric.verifyIdentity({
      title: "Confirmar identidad",
      reason: "Confirma tu identidad para continuar",
      negativeButtonText: "Cancelar",
    });

    const payload = JSON.stringify({
      access_token: token,
      enrolledAt: Date.now(),
    } satisfies VaultPayload);
    // Proactive clean: remove stale invalidated blob before setData to avoid
    // "Failed to encrypt credentials: null" on some Android Keystore states.
    try {
      await NativeBiometric.deleteData({ key: this.keyFor(type) });
    } catch {
      // ignore missing key — idempotent
    }
    await NativeBiometric.setData({
      key: this.keyFor(type),
      value: payload,
      accessControl: AccessControl.BIOMETRY_ANY,
      title: "Activar biometría",
      authValidityDuration: 1,
      negativeButtonText: "Cancelar",
    });

    localStorage.setItem(`tenso.biometric.hasPrompted.${type}`, "1");
    localStorage.setItem("tenso.session.lastType", type);
    localStorage.setItem(`tenso.biometric.cancelCount.${type}`, "0");
    this._lastCode = null;
  }

  async unlock(type: BiometricType): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) return false;
    this._lastCode = null;

    let rawValue: string;
    try {
      const { value } = await NativeBiometric.getSecureData({
        key: this.keyFor(type),
        reason: "Confirma tu identidad para continuar",
        title: "Desbloquear Tenso",
      });
      rawValue = value;
    } catch (e: unknown) {
      const raw =
        (e as { code?: unknown; errorCode?: unknown })?.code ??
        (e as { errorCode?: unknown })?.errorCode;
      const code =
        typeof raw === "string" ? Number(raw) : (raw as number | undefined);
      this._lastCode =
        typeof code === "number" && !Number.isNaN(code) ? code : null;
      if (code === BiometricAuthError.NO_PROTECTED_CREDENTIALS_FOUND) {
        await this.handleBiometryChanged(type);
        return false;
      }
      if (
        code === BiometricAuthError.USER_CANCEL ||
        code === BiometricAuthError.AUTHENTICATION_FAILED ||
        code === BiometricAuthError.SYSTEM_CANCEL ||
        code === BiometricAuthError.APP_CANCEL
      ) {
        // D-04 permissive: keep vault, backoff cancelCount
        const k = `tenso.biometric.cancelCount.${type}`;
        localStorage.setItem(
          k,
          String(Number(localStorage.getItem(k) ?? "0") + 1),
        );
        return false;
      }
      if (
        code === BiometricAuthError.USER_LOCKOUT ||
        code === BiometricAuthError.USER_TEMPORARY_LOCKOUT
      ) {
        return false;
      }
      // Unknown biometric error — keep vault, don't delete
      return false;
    }

    let parsed: VaultPayload;
    try {
      parsed = JSON.parse(rawValue) as VaultPayload;
    } catch {
      await this.deleteScoped(type);
      this.authStore.clearSession();
      this._lastCode = null;
      return false;
    }

    const accessToken = parsed.access_token;
    if (!accessToken) {
      await this.deleteScoped(type);
      this.authStore.clearSession();
      this._lastCode = null;
      return false;
    }

    // exp check before network
    const exp = decodeJwtExp(accessToken);
    if (exp !== null && exp * 1000 <= Date.now()) {
      await this.deleteScoped(type);
      this.authStore.clearSession();
      this._lastCode = null;
      return false;
    }

    // Temporarily set vault token so AuthApi.me() authenticates with it via interceptor
    const prevToken = this.authStore.accessToken();
    const prevUser = this.authStore.user();
    const needsTemp = prevToken !== accessToken;
    if (needsTemp) {
      this.authStore.updateToken(accessToken);
    }

    // Online validation fail-closed — keep vault on network error
    try {
      const user = await firstValueFrom(this.authApi.me());
      this.authStore.setSession(accessToken, user);
      localStorage.setItem(`tenso.biometric.cancelCount.${type}`, "0");
      localStorage.setItem("tenso.session.lastType", type);
      localStorage.setItem("tenso.biometric.lastSuccessAt", String(Date.now()));
      try {
        sessionStorage.setItem("tenso.idle.sessionAlive", "1");
      } catch {
        // ignore sessionStorage unavailable
      }
      this._lastCode = null;
      return true;
    } catch (err: unknown) {
      const status =
        err instanceof HttpErrorResponse
          ? err.status
          : (err as { status?: number })?.status;
      if (status === 401) {
        await this.deleteScoped(type);
        this.authStore.clearSession();
        this._lastCode = 401;
        return false;
      }
      // Offline / network error — keep vault steel, revert to previous session
      if (needsTemp) {
        if (prevToken && prevUser) {
          this.authStore.setSession(prevToken, prevUser);
        } else {
          this.authStore.clearSession();
        }
      }
      this._lastCode = status === 0 ? 0 : null;
      return false;
    }
  }

  async disable(type: BiometricType): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      localStorage.removeItem(`tenso.biometric.hasPrompted.${type}`);
      return;
    }
    try {
      await NativeBiometric.deleteData({ key: this.keyFor(type) });
    } catch {
      // idempotent — ignore missing key
    }
    localStorage.removeItem(`tenso.biometric.hasPrompted.${type}`);
  }

  async listenBiometryChange(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    if (this.biometryChangeRegistered) return;
    try {
      const handle = await NativeBiometric.addListener(
        "biometryChange",
        async () => {
          // Al cambiar biometría del OS, borrar ambos vaults y cerrar sesión (fail-closed)
          try {
            await NativeBiometric.deleteData({ key: this.keyFor("staff") });
          } catch {
            // ignore
          }
          try {
            await NativeBiometric.deleteData({ key: this.keyFor("socio") });
          } catch {
            // ignore
          }
          this.authStore.clearSession();
          this.toast.show("Biometría cambiada — vuelve a configurarla", "info", 4000);
        },
      );
      this.biometryChangeHandle = handle as unknown as { remove: () => Promise<void> };
      this.biometryChangeRegistered = true;
    } catch {
      // ignore addListener failure
    }
  }

  async removeBiometryChangeListener(): Promise<void> {
    if (this.biometryChangeHandle) {
      try {
        await this.biometryChangeHandle.remove();
      } catch {
        // ignore
      }
      this.biometryChangeHandle = null;
      this.biometryChangeRegistered = false;
    }
  }

  private async handleBiometryChanged(type: BiometricType): Promise<void> {
    await this.deleteScoped(type);
    this.authStore.clearSession();
    this.toast.show("Biometría cambiada — vuelve a configurarla", "info", 4000);
  }

  private async deleteScoped(type: BiometricType): Promise<void> {
    try {
      await NativeBiometric.deleteData({ key: this.keyFor(type) });
    } catch {
      // ignore missing key
    }
  }
}
